import { readFile } from "node:fs/promises";
import { extname } from "node:path";
import { parseFrontmatter } from "@vortex-os/core";
import { extractWikiLinks } from "./extract.js";
import { buildFileIndex, resolveLink } from "./resolve.js";
import type { BuildIndexOptions } from "./resolve.js";
import type { BrokenLink, LinkCheckResult } from "./types.js";

/**
 * Options for {@link checkDirectory}.
 */
export interface CheckDirectoryOptions extends BuildIndexOptions {}

/**
 * Scan every `.md` file under `rootDir`, extract wiki links, and
 * report which ones cannot be resolved (or resolve to multiple files).
 *
 * This is read-only — it does not modify any file. The function returns
 * a structured result so hosts can decide whether to log, fail a build,
 * propose rewrites, etc.
 *
 * Pass `caseInsensitive: true` when wiki-link casing may differ from
 * the on-disk path (e.g. content migrated from a vault with different
 * directory naming conventions).
 */
export async function checkDirectory(
  rootDir: string,
  options: CheckDirectoryOptions = {},
): Promise<LinkCheckResult> {
  const index = await buildFileIndex(rootDir, options);
  const broken: BrokenLink[] = [];
  const ambiguous: BrokenLink[] = [];

  let filesScanned = 0;
  let totalLinks = 0;
  let resolved = 0;

  for (const paths of index.byBasename.values()) {
    for (const path of paths) {
      // Only markdown files carry wiki-link text. Non-md entries in the
      // index (when `additionalExtensions` is set) are resolution targets,
      // not scan sources.
      if (extname(path) !== ".md") continue;
      filesScanned++;
      const raw = await readFile(path, "utf8");
      const { body } = parseFrontmatter(raw);
      const links = extractWikiLinks(body);
      totalLinks += links.length;
      for (const link of links) {
        const outcome = resolveLink(link.name, index, { sourcePath: path });
        if (outcome.kind === "unique") {
          resolved++;
        } else if (outcome.kind === "not-found") {
          broken.push({ sourcePath: path, link, reason: "not-found" });
        } else {
          ambiguous.push({
            sourcePath: path,
            link,
            reason: "ambiguous",
            candidates: outcome.candidates,
          });
        }
      }
    }
  }

  return { filesScanned, totalLinks, resolved, broken, ambiguous };
}

/**
 * Group broken links by target name and return counts in descending order.
 * Useful for quickly identifying the most-cited missing pages.
 */
export function topBrokenTargets(
  broken: readonly BrokenLink[],
  limit = 20,
): readonly { readonly name: string; readonly count: number }[] {
  const counts = new Map<string, number>();
  for (const b of broken) {
    counts.set(b.link.name, (counts.get(b.link.name) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    // Code-unit tiebreak (NOT localeCompare, whose order varies by host
    // locale/ICU version), matching the deterministic walk order in resolve.ts.
    .sort((a, b) => b.count - a.count || (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
    .slice(0, limit);
}
